import {
  AfterViewInit,
  Component,
  ElementRef,
  HostListener, Inject,
  OnDestroy,
  OnInit,
  ViewChild, ViewChildren,
} from '@angular/core';
import {ActivatedRoute, NavigationStart, Router} from '@angular/router';
import {DOCUMENT, Location} from '@angular/common';
import {Subscription, timer} from 'rxjs';
import {delay, filter, finalize} from 'rxjs/operators';
import scrollIntoView from 'scroll-into-view-if-needed';

import {JASO} from '../shared/defines/gukeo.const';
import {
  DETAIL_POPUP_DELAY, HISTORY_KEY_VIEW_WORD,
  HISTORY_KEY_SEARCH_WORD,
  LIST_TYPE,
  SearchParam,
  UI_RELOAD_HEIGHT, BookmarkChange
} from '../shared/defines/client.const';
import {ListLoader} from '../shared/interfaces/list-loader';
import {
  generateUrlWithQueryParams,
  GetSearchFromQuery,
  listItemId,
} from '../shared/defines/client.func';
import {
  WORD_SCROLL_SPEED,
  MENU_PAGE,
  PRELOAD_RATIO, LIST_SCROLL_INTERVAL, BODY_HEIGHT_VALUE, BODY_HEIGHT_CAL, EXT_HEIGHT_CAL, SIDE_HEIGHT_DELTA,
} from '../shared/defines/desktop.const';

import {SimpleWord} from '../shared/models/simple-word.model';
import {Article} from "../shared/models/article.model";
import {VoiceService} from '../shared/services/voice/voice.service';
import {OptionService} from '../shared/services/option/option.service';
import {WordService} from '../shared/services/word/word.service';
import {WordBookmarkService} from '../shared/services/word/word-bookmark.service';
import {WordHistoryService} from '../shared/services/word/word-history.service';
import {LayoutMgrService} from "../shared/services/layout-mgr/layout-mgr.service";
import {GlobalEventService} from "../shared/services/global-event/global-event.service";
import {SearchBoxComponent} from "../shared/components/search-box/search-box.component";
import {WordListItemComponent} from "../shared/components/word-list-item/word-list-item.component";
import {LastIdService} from "../shared/services/last-id/last-id.service";
import {STAR_FILTER} from "../shared/defines/protocol";
import {WordDetailComponent} from "../shared/components/word-detail/word-detail.component";


@Component({
  selector: 'app-word',
  templateUrl: './word.component.html',
  styleUrls: ['./word.component.scss']
})
export class WordComponent implements OnInit, AfterViewInit, OnDestroy {
  @ViewChild('container') container?: ElementRef; // ngAfterViewInit() 에서 사용 가능
  @ViewChild('reload') reload?: ElementRef; // ngAfterViewInit() 에서 사용 가능
  @ViewChild('innerScroll') innerScroll?: ElementRef; // ngAfterViewInit() 에서 사용 가능

  // 화면 사이즈 맞춤에 사용
  @ViewChild('extContainer') extContainer?: ElementRef;
  @ViewChild(SearchBoxComponent) extHeader?: SearchBoxComponent;
  @ViewChild('extBody') extBody?: ElementRef;
  @ViewChild('extBody2') extBody2?: ElementRef;
  @ViewChild('wordDetail', { static: false }) wordDetail?: WordDetailComponent;
  @ViewChildren(WordListItemComponent) listItem?: WordListItemComponent[];

  // 자식 컴포넌트에서 사용하는 변수
  protected thisPageAddr = '/word';
  protected historyKeySearch = HISTORY_KEY_SEARCH_WORD;
  protected historyKeyView = HISTORY_KEY_VIEW_WORD;
  protected jasoIdxSecond = true;
  protected getMenuPage(): MENU_PAGE {
    return MENU_PAGE.WORD;
  }
  protected getBookmarkMenuPage(): MENU_PAGE {
    return MENU_PAGE.WORD_BOOKMARK;
  }
  protected modParam(param: SearchParam): SearchParam {
    return param;
  }
  protected setLastId(detail_id: string | null, list_id: string | null): void {
    this.lastIdSvc.setLastWordId(detail_id, list_id);
  }
  protected clearLastId(): void {
    this.lastIdSvc.clearLastWordId();
  }

  protected selectedMenu: MENU_PAGE = MENU_PAGE.NONE;

  protected noResultTxt = '단어가 없습니다.';

  // 라우터에서 넣어주는 값. (TODO: 초기화에 유의해야 함)
  private queryParams: SearchParam | null = null;
  private queryParams$: Subscription | null = null;
  private routeParams$: Subscription | null = null;

  private type: LIST_TYPE | null = null;


  // ListLoader 설정
  public ll: ListLoader<SimpleWord> | null = null;
  private getNextList$: Subscription | null | undefined = null;
  private needReload = false;
  protected get lastId(): string | null {
    const ll = this.ll?.getList() ?? null;
    return ll ? ll[ll.length - 1].id : null;
  }

  // 상세화면 표시 관련 정의
  public curDetail: string | Article | null = null; // 현재 선택된 단어의 id

  protected autoPlay = false; // 화면에 처음 들어왔을 때, 자동 재생을 막는 변수.
  private lazyAutoPlay = false;

  protected showLevel = true;

  protected get wordLevelAll(): boolean {
    return this.option.wordLevel === STAR_FILTER.ALL;
  }

  protected get curDetailWord(): string | null {
    return typeof this.curDetail === "string" ? this.curDetail : null;
  }

  protected get curDetailArticle(): Article | null {
    return this.curDetail instanceof Article ? this.curDetail : null;
  }

  protected isNoResult(): boolean {
    return (this.ll?.IsComplete() ?? false) && (this.ll?.getList()?.length === 0 ?? false);
  }

  // 자소 인덱스
  public curJasoIdx: JASO | null = null;

  // 검색창에 넣는 값
  public keyword: string | null = null;
  public placeholder = '단어를 검색 합니다.';
  protected scrollTop = true;   // 스크롤이 맨 위에 있는지 여부, 검색창의 호랑이를 헤더 아래로 감출 때 사용한다.

  // 라우딩 처리
  private router$?: Subscription | null = null;

  // 화면 크기 변경
  // private resize$: Subscription | null = null;

  // 화면 크기 조정
  private bodyHeight = UI_RELOAD_HEIGHT;

  // 스크롤 처리
  private scrollDownInterval: any = null;

  protected showScrollDown = false;

  constructor(
    @Inject(DOCUMENT) private document: Document,
    private router: Router,
    private route: ActivatedRoute,
    private location: Location,
    private voice: VoiceService,
    private option: OptionService,
    private wordSvc: WordService,
    private bmSvc: WordBookmarkService,
    private historySvc: WordHistoryService,
    private globalEventSvc: GlobalEventService,
    protected lastIdSvc: LastIdService,
    public lm: LayoutMgrService,
  ) {
    // super(globalEventSvc);

    /******************************************************************************************
     * 라우터 파리미터 변경 핸들러 - 주소 뒤의 파라미터를 받아오는 곳. ( ?keyword=... )
     * 입력된 파라미터가 없어도 처음 초기화 할 때에도 불려진다.
     * 처음 화면에 들어올 때, 라우터 주소 변견 핸들러보다 더 먼저 불려진다. (이상하지만 그렇다)
     *
     * router.navigate()로 같은 주소를 호출하면 이 코드가 다시 불려진다.
     */
    this.queryParams$ = this.route.queryParams
      .subscribe(params => {
        this.queryParams = {...params};
        delete this.queryParams.serial;
        console.log('WordComponent::queryParams =', this.type, params, this.queryParams);

        if (this.type) {
          // 라우터 주소 변경 핸들러로 들어와서 init()이 처음 불려진 경우 = 이미 화면이 목록이 불려진 경우 = 목록이 초기화되어있는 경우!
          // 이 조건을 안 걸면 init()가 두번 불려진다.

          // 현재 목록에서 지정한 단어 항목이 존재하는지 확인
          if (this.queryParams?.id) {
            // id가 있다는 의미는, 목록처리는 필요 없고, 상세창 처리만 하겠다는 의미.
            console.log('WordComponent::queryParams.id =', this.queryParams.id);

            const processParams = () => {
              if (this.queryParams?.id) {
                if (this.ll?.getById(this.queryParams.id)) {
                  // 현재 로딩 목록에 존재한다면
                  this.CloseDetail();
                  this.OpenDetail(this.queryParams.id);

                  this.scrollToWord(this.queryParams.id);
                }
                else {
                  this.init();
                }
              }
              else {
                throw new Error('WordComponent queryParams.id is null');
              }
            };

            if (this.ll?.IsLoading()) {
              // 이 시점에서 아직 목록 로딩이 안 끝났을 수 있다.
              this.ll.GetNextList().pipe(
                delay(DETAIL_POPUP_DELAY),
              ).subscribe(processParams);
            } else {
              processParams();
            }
          } else {
            // 다시 서버에서 받아온다.
            this.init();
          }
        }
      });

    /******************************************************************************************
     * 라우터 주소 변경 핸들러 - 라우터 주소 읽어오는 곳.
     * 라우터 주소에 포함된 파라미터가 변경되어도 불려진다. (여기서는 type만 변경되어도 불려짐)
     *
     * queryParams가 바뀐 것(주소뒤의 파리미터가 변경된 경우)으로는 여기가 불려지지 않는다.
     */
    route.params.subscribe(params => {
      console.log('WordComponent::params =', params, this.queryParams);
      this.type = params['type'];

      this.init();
    });

    // lm.Reset();
  }

  ngOnInit(): void {

    // popup창 테스트 코드
    // timer(2000).subscribe(() => {
    //   // console.warn('MsgPopup 테스트');
    //   this.lm.OpenMsgPopup({
    //     msg: 'MsgPopup 테스트',
    //     title: '타이틀',
    //     btnOk: '확인',
    //     btnCancel: '취소',
    //     btnError: '에러',
    //   }).subscribe((result) => {
    //     console.log('result =', result);
    //   });
    // });

    // 링크 팝업창 테스트
    // timer(2000).subscribe(() => {
    //   this.lm.OpenLinkPopup({
    //     type: LINK_TYPE.WORD,
    //     id: '가공3',
    //   }).subscribe((result) => console.log('result =', result));
    // });

    // 페이지를 이동하면 소리를 멈추도록 구현
    // TODO: 이 부분은 좀 더 정밀하게 할 필요가 있다. 내 컴포넌트(WordComponent)인 경우에만 처리하도록 수정할 필요가 있다.
    this.router$ = this.router.events.pipe(
      filter(event => event instanceof NavigationStart),
    ).subscribe((event) => {
      this.onLeave();
    });

    // this.resize$ = this.resizeSvc.screenHeight$.subscribe(height => {
    //   console.log('resize::height = ', height);
    //   if (this.showDetail) {
    //     const elContainer = this.container.nativeElement;
    //     const el = document.getElementById(wordSelectId(this.curDetail));
    //     this.detailHeight = getDetailHeight(elContainer, el, this.footer.height);
    //   }
    // });

    // if (this.main) {
    //   this.InitFocusMain(this.main);
    // }
    // else {
    //   console.error('WordComponent::ngOnInit() main is null');
    // }
  }

  ngAfterViewInit() {
    this.updateExtHeight();

    // 최초 로딩 시, 화면 배치가 어그러지는 것을 고치기위한 코드. (TODO: 효과 있는지 확인 후 필요시 제거)
    timer(2000).subscribe(() => {
      this.updateExtHeight();
    });
  }

  ngOnDestroy(): void {
    if (this.getNextList$) {
      this.getNextList$.unsubscribe();
      this.getNextList$ = null;
    }

    // 나가기 전에 창 닫기처리.
    this.CloseDetail();

    // router 이동 체크를 중지함.
    if (this.router$) {
      this.router$.unsubscribe();
      this.router$ = null;
    }
    if (this.queryParams$) {
      this.queryParams$.unsubscribe();
      this.queryParams$ = null;
    }
    if (this.routeParams$) {
      this.routeParams$.unsubscribe();
      this.routeParams$ = null;
    }
    // this.ExitFocusMain();

    if (this.scrollDownInterval) {
      clearInterval(this.scrollDownInterval);
      this.scrollDownInterval = null;
    }

    // 소리 재생 중지
    // TODO: routerStop$ 처리 시 하고 있는데 또 반복적으로 해야 할까?
    this.voice.Stop();
  }

  private resetParams(): void {
    this.curDetail = null;
    this.curJasoIdx = null;
    this.keyword = null;
    // delete this.queryParams?.hanja;
  }

  /**
   * 저장된 searchParam(라우팅에서 전달받은 옵션)을 이용해서 페이지를 재 초기화하고 다시 로딩한다.
   */
  private init(): void {
    console.warn('WordComponent::init()', this.type, this.queryParams);
    let param: SearchParam;

    switch (this.type) {
      case LIST_TYPE.SERVER:
        // id 파라미터가 있는 경우 이력화면으로 다시 이동해서 표시한다.
        // if (this.queryParams?.id) {
        //   this.historySvc.addHistory(this.queryParams.id);
        //   this.router.navigate([THIS_PAGE_ADDR, LIST_TYPE.HISTORY], {
        //     queryParams: this.queryParams,
        //   }).then();
        //   return;
        // }

        this.selectedMenu = this.getMenuPage();
        this.ll = this.wordSvc;
        // this.placeholder = '단어를 검색 합니다.';
        [param, this.curJasoIdx, this.keyword] = GetSearchFromQuery(this.queryParams);
        // Spec: docs/page_common_logic.md > 선택 URL 히스토리와 start/id 보존
        // init() must preserve URL `start`; `id` only fills the anchor when `start` is absent.
        // 상세는 별도 핸들러(loadList)에서 queryParams.id로 복원한다.
        if (this.queryParams?.id && !this.queryParams.start) {
          console.warn(
            `WordComponent::init() start anchor missing; deriving from id=${this.queryParams.id}.`,
            { queryParams: this.queryParams },
          );
          param.start = this.queryParams.id;
        }

        if (this.queryParams?.hanja) {
          this.resetParams();
          param.hanja = this.queryParams.hanja;
          // 음(hangul)은 한자가 있을 때만 함께 보존한다.
          if (this.queryParams.hangul) {
            param.hangul = this.queryParams.hangul;
          }
        }

        console.warn(`WordComponent::init() keyword=${this.keyword}, jasoIdx=${this.curJasoIdx}, param=${JSON.stringify(param)}`, this);
        this.ReloadList(param);
        break
      case LIST_TYPE.BOOKMARK:
        this.selectedMenu = this.getBookmarkMenuPage();
        this.ll = this.bmSvc;
        this.curJasoIdx = null;
        // this.placeholder = '단어를 검색 합니다.';
        [param, this.curJasoIdx, this.keyword] = GetSearchFromQuery(this.queryParams);
        console.log('LIST_TYPE.BOOKMARK', param, this.curJasoIdx, this.keyword);
        this.ReloadList(param);
        break;
      case LIST_TYPE.HISTORY:
        this.selectedMenu = MENU_PAGE.WORD_HISTORY;
        this.ll = this.historySvc;
        this.curJasoIdx = null;
        // this.placeholder = '단어를 검색 합니다.';
        param = (this.queryParams && 'id' in this.queryParams) ? { id: this.queryParams.id } : {};
        this.ReloadList(param);
        break;
      default:
        console.error('Invalid LIST_TYPE =', this.type);
    }
  }

  private onLeave() {
    this.voice.Stop();

    const elContainer = this.container?.nativeElement;
    if (elContainer) {
      if (this.queryParams?.keyword || this.queryParams?.hanja) {
        this.clearLastId();
        return;
      }

      const listItem = this.listItem?.find(item => elContainer.scrollTop <= item.offsetTop);
      if (!listItem?.word?.id) {
        this.clearLastId();
        return;
      }

      this.setLastId(this.curDetailWord, listItem.word.id);
      // Spec: docs/page_common_logic.md > 선택 URL 히스토리와 start/id 보존
      // Keep `id`; `start` restores list position, `id` restores selected detail.
      const queryParam: SearchParam = {
        ...(this.queryParams ?? {}),
        start: listItem.word.id,
      }

      const basePath = this.router.url.split('?')[0];
      const urlWithParams = generateUrlWithQueryParams(basePath, queryParam);
      console.log('WordComponent::onLeave() urlWithParams =', urlWithParams);

      this.location.replaceState(urlWithParams);
    }
    else {
      console.error('elContainer is null');
    }
  }

  public onGoHistory(): void {
    this.type = null; // 생성자의 queryParams 쪽으로 init()가 불려지는 것을 막음.
    this.router.navigate([this.thisPageAddr, LIST_TYPE.HISTORY]).then();
  }

  /**
   * 마지막 child 의 View가 초기화 되었을 때 호출된다.
   * @param word
   */
  onChildViewInit(word: SimpleWord) {
    // console.warn('onChildViewInit()', word, this.innerScroll?.nativeElement.offsetHeight, this.container?.nativeElement.offsetHeight);

    if (word.id === this.lastId) {
      timer(0).subscribe(() => {
        this.showScrollDown = this.innerScroll?.nativeElement.offsetHeight > this.container?.nativeElement.offsetHeight;
      });
    }
  }

  /****************************************************************************
   * 브라우저 크기 변경 처리.
   ****************************************************************************/


  // 'resize' 이벤트를 감지하고 이벤트 발생 시 updateHeight 메소드를 호출합니다.
  @HostListener('window:resize', ['$event'])
  onResize(event: Event) {
    this.updateExtHeight();
  }

  private updateExtHeight(): void {
    if (this.extContainer && this.extHeader) {
      const containerHeight = this.extContainer.nativeElement.offsetHeight;
      const headerHeight = this.extHeader.offsetHeight;
      this.bodyHeight = containerHeight - headerHeight - EXT_HEIGHT_CAL;

      if (this.extBody) {
        this.extBody.nativeElement.style.height = this.bodyHeight + 'px';
      }

      if (this.extBody2) {
        const bodyHeight2 = Math.max(
          BODY_HEIGHT_VALUE - SIDE_HEIGHT_DELTA,
          this.bodyHeight + BODY_HEIGHT_CAL);
        this.extBody2.nativeElement.style.setProperty('--side-height', bodyHeight2 + 'px');
      }
    }
  }

  /****************************************************************************
   * 단어 목록 로딩 처리.
   ****************************************************************************/

  public onSelectWord(word_id: string, event: MouseEvent) {
    event.preventDefault();
    event.stopPropagation();
    this.globalEventSvc.Click(event, true);

    if (this.curDetail !== word_id && this.queryParams?.id === word_id) {
      // 단어 도움말이 열려있는데, 라우터에는 선택된 단어가 표시되고 있을 때
      this.OpenDetail(word_id);
    }
    else {
      const queryParams = {
        ...this.queryParams,
        id: word_id
      };
      // Spec: docs/page_common_logic.md > 선택 URL 히스토리와 start/id 보존
      this.router.navigate([this.thisPageAddr, this.type], {queryParams}).then();
    }

    // 이미 로딩된 단어는 바로 소리를 재생한다.
    if (this.curDetail === word_id
      && this.voice.wordId !== word_id  // 소리가 재생되고 있지 않을 때에만 재생한다.
    ) {
      this.wordDetail?.PlayWord();
    }
  }

  public onSearch(param: SearchParam): void {
    console.log('WordComponent::onSearch()', param);

    // 모든 검색은 단어 목록에서 검색
    // NOTE: 이전에는 이력 화면, 북마크 내에서도 검색이 가능했음.
    this.router.navigate([this.thisPageAddr, LIST_TYPE.SERVER], {
      queryParams: param,
    }).then();

    this.clearLastId();
  }

  public ReloadList(param: SearchParam): void {
    // console.warn('WordComponent::ReloadList()', param, this.queryParams);

    this.showScrollDown = false;

    this.ll?.ResetList({
      ...this.modParam(param),
      level: this.option.wordLevel}
    );
    this.loadList(true);

    this.autoPlay = false;
    this.lazyAutoPlay = false;
  }

  private loadList(cancel = false): void {
    // console.log('loadList()', this.queryParams);
    if (this.getNextList$) {
      console.warn('WordComponent::loadList() already call loadList!');
      if (cancel) {
        this.getNextList$.unsubscribe();
      }
      else {
        return;
      }
    }

    if (this.ll?.IsComplete()) {
      console.warn('WordComponent::loadList() already load all word!');
      return;
    }

    this.getNextList$ = this.ll?.GetNextList()
      .pipe(
        filter(first => first),
        // delay(DETAIL_POPUP_DELAY),
        finalize(() => {
          // console.warn('getNextList$ = null');
          this.getNextList$ = null;

          if (this.needReload) {
            timer(0).subscribe(() => {
              this.needReload = false;
              this.loadList();
            });
          }
        })
      ).subscribe(
        first => {

          if (this.ll?.getTotalCount() === 0) {
            this.CloseDetail();
          }
          else if (this.queryParams?.id) {
            // 바로 보기 상태라면, 서버로부터 로딩이 완료되고 나서 바로 상세 화면을 연다.
            // TODO: init() 쪽으로 코드를 옮기기.
            //  앱에서와 다르게 (목록과 상세 화면이 분리되어 있는 경우) 여기까지 와서 OpenDetail을 할 필요가 없어 보인다.
            const targetId = String(this.queryParams.id);
            if (!this.ll?.getById(targetId)) {
              console.warn(
                `WordComponent::loadList() id=${targetId} not in loaded list; OpenDetail will fetch separately.`,
                { listSize: this.ll?.getList()?.length ?? 0, queryParams: this.queryParams },
              );
            }
            this.OpenDetail(this.queryParams.id);
          }
          else {
            // 로딩 된 항목이 한개 이상이라면 첫번째 항목을 보여준다.
            const first = this.ll?.getList()?.at(0);
            first && this.OpenDetail(first.id, false);
          }
        }
      );
  }

  public CloseDetail(bookmark: boolean | null = null): void {
    this.curDetail = null;
  }

  /****************************************************************************
   * 책갈피 실시간 추가 삭제
   ****************************************************************************/
  protected onBookmarkChange(bookmarkChange: BookmarkChange): void {
    if (this.type === LIST_TYPE.BOOKMARK) {
      // 북마크 화면이라면
      this.init();
    }
  }

  /****************************************************************************
   * 일정 범위 이상 스크롤되면 남은 단어를 자동 로딩한다.
   ****************************************************************************/

  protected onScrollTop($event: Event): void {
    const target = $event.target as HTMLElement;

    // console.log('onScrollTop() ', target.scrollTop);
    this.scrollTop = target.scrollTop === 0; // 수직 스크롤 위치
  }

  protected onScroll($event: Event): void {
    if (this.container === undefined) {
      throw new Error('WordComponent::onScroll() - #container does not exist!!');
    }

    if (this.ll && !this.ll.IsLoading() && !this.ll.IsComplete() && this.reload) {
      const tar = $event.target || $event.srcElement || $event.currentTarget;
      const elContainer = this.container.nativeElement;
      if (tar !== elContainer) {
        console.error('WordComponent::onScroll() - wrong structure!!');
      }

      const elReload = this.reload.nativeElement;
      // NOTE: 앱은 스크롤되는 윗 부분에 빈 공간이 있지만, 책상형은 공간이 없다. 그래서 innerHeight 반영하지 않는다.
      // console.warn('onScroll() ', elReload.offsetTop, elContainer.scrollTop, this.bodyHeight, elContainer.scrollTop + PRELOAD_RATIO * this.bodyHeight);
      if (elReload.offsetTop < elContainer.scrollTop + PRELOAD_RATIO * this.bodyHeight) {
        if (this.getNextList$ === null) {
          this.loadList();
        }
        else {
          // 스크롤 중에 이미 로딩이 진행되고 있으면, 로딩이 끝난 뒤에 다시 로딩이 되도록 요청. (비동기처리 순서 꼬임 방지)
          this.needReload = true;
        }
      }

    }
  }

  onClickRope($event: MouseEvent) {
    console.warn('onClickRope()', $event);
    $event.preventDefault();
    $event.stopPropagation();
    this.globalEventSvc.Click($event, true);

    this.container?.nativeElement.scrollTo({
      top: 0,
      behavior: 'smooth',
      // behavior: 'auto',
    });
  }

  onStartScrollDown($event: MouseEvent|TouchEvent) {
    // console.warn('onStartScrollDown()', $event);
    $event.preventDefault();
    $event.stopPropagation();
    this.globalEventSvc.Click($event);

    // 한번은 바로 내려간다.
    this.container?.nativeElement.scrollTo({
      top: this.container.nativeElement.scrollTop + WORD_SCROLL_SPEED * 2,
      behavior: 'auto',
    });

    this.scrollDownInterval = setInterval(() => {
      this.container?.nativeElement.scrollTo({
        top: this.container.nativeElement.scrollTop + WORD_SCROLL_SPEED,
        behavior: 'auto',
      });
    }, LIST_SCROLL_INTERVAL);
  }

  onEndScrollDown($event: MouseEvent|TouchEvent) {
    // console.warn('onEndScrollDown()', $event);
    clearInterval(this.scrollDownInterval);
    this.scrollDownInterval = null;
  }

  /**
   * 단어를 선택하면, 왼쪽 목록에서 해당 단어가 보이도록 스크롤 시킨다.
   * @param word_id
   * @private
   */
  private scrollToWord(word_id: string): void {
    const el: HTMLElement | null = this.document.getElementById(listItemId(word_id));

    if (el) {
      scrollIntoView(el, {
        behavior: 'smooth',
        block: 'start',
        scrollMode: 'if-needed',
      });
    }
    else {
      console.error('WordComponent::scrollToWord() - not found element', word_id);
    }
  }

  /****************************************************************************
   * 단어 상세 보기 처리 코드
   ***************************************************************************/

  public OpenDetail(word_id: string, addHistory = true): void {
    this.curDetail = word_id;
    if (!(this.ll instanceof WordHistoryService) && addHistory) {
      this.historySvc.addHistory(word_id);
      console.log('add history', this.historySvc);
    }

    /**
     * 화면에 처음 들어올 때는 자동 재생을 막고, 그 다음부터 자동 재생 되도록 한다.
     * 화면에 처음 들어올 때, 무조건 첫번째 항목을 상세 화면에 표시하도록 되어있다. 이때는 소리 안 나야 함.
     */
    if (!this.lazyAutoPlay) {
      this.lazyAutoPlay = true;
    }
    else {
      this.autoPlay = true;
    }

    // console.warn('OpenDetail()', word_id, addHistory, this.autoPlay);
  }


  /****************************************************************************
   * 기타
   ***************************************************************************/


  protected max(a: number, b: number): number {
    return Math.max(a, b);
  }

}
